import React, { useState, useRef, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from '../firebaseConfig';
import { renderMarkdown } from '../utils/markdown';

const WELCOME = "Hi! I'm Buzz AI 🐝 — I can help you understand your pipeline, spot logical issues, or guide you through building new flows. What would you like to know?";

const SUGGESTIONS = [
  'Are there any dead-ends in my pipeline?',
  'How do I set up email notifications?',
  'Explain how approval gates work',
  'What should a manager agent do?',
];

function mkSession() {
  return {
    id: `bsess_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    title: 'New chat',
    messages: [{ role: 'assistant', text: WELCOME }],
    createdAt: new Date().toISOString(),
  };
}

function loadSessions() {
  try { return JSON.parse(localStorage.getItem('projectly_buzz_sessions') || '[]'); }
  catch { return []; }
}


// The prompt, model choice and workspace context all live server-side in the
// buzzChat callable now — cheap Gemini tokens, scoped to the caller's access.
const callBuzz = httpsCallable(functions, 'buzzChat');

export default function BuzzAI({ onClose, workspaceId, user }) {
  // Compute initial state once from localStorage
  const [init] = useState(() => {
    const stored = loadSessions();
    const sessions = stored.length > 0 ? stored : [mkSession()];
    const savedId  = localStorage.getItem('projectly_buzz_current');
    const currentId = (savedId && sessions.find(s => s.id === savedId)) ? savedId : sessions[0].id;
    return { sessions, currentId };
  });

  const [sessions,   setSessions]   = useState(init.sessions);
  const [currentId,  setCurrentId]  = useState(init.currentId);
  const [input,      setInput]      = useState('');
  const [typing,     setTyping]     = useState(false);
  const [minimized,  setMinimized]  = useState(false);
  const [view,       setView]       = useState('chat'); // 'chat' | 'sessions'
  const [allowance,  setAllowance]  = useState(null);   // today's usage vs daily cap
  const [pos,        setPos]        = useState(() => ({
    x: Math.max(20, window.innerWidth  - 420),
    y: Math.max(20, window.innerHeight - 580),
  }));
  const [size, setSize] = useState({ width: 364, height: 540 });

  const isDragging  = useRef(false);
  const isResizing  = useRef(false);
  const dragStart   = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const resizeStart = useRef({});
  const bottomRef   = useRef(null);

  // Global drag + resize handlers — set up once on mount
  useEffect(() => {
    const onMove = (e) => {
      if (isDragging.current) {
        setPos({
          x: Math.max(0, Math.min(window.innerWidth  - size.width,  dragStart.current.posX + e.clientX - dragStart.current.mouseX)),
          y: Math.max(0, Math.min(window.innerHeight - 44,           dragStart.current.posY + e.clientY - dragStart.current.mouseY)),
        });
      }
      if (isResizing.current) {
        const { mouseX, mouseY, w, h } = resizeStart.current;
        setSize({ width: Math.max(280, w + e.clientX - mouseX), height: Math.max(240, h + e.clientY - mouseY) });
      }
    };
    const onUp = () => { isDragging.current = false; isResizing.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.width]);

  // Persist sessions + currentId
  useEffect(() => { localStorage.setItem('projectly_buzz_sessions', JSON.stringify(sessions)); }, [sessions]);
  useEffect(() => { localStorage.setItem('projectly_buzz_current',  currentId);                }, [currentId]);

  // Auto-scroll
  useEffect(() => {
    if (view === 'chat' && !minimized) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sessions, typing, view, minimized]);

  const currentSession = sessions.find(s => s.id === currentId) || sessions[0];
  const messages       = currentSession?.messages || [];

  const startDrag = useCallback((e) => {
    if (e.target.closest('button') || e.button !== 0) return;
    e.preventDefault();
    isDragging.current = true;
    dragStart.current  = { mouseX: e.clientX, mouseY: e.clientY, posX: pos.x, posY: pos.y };
  }, [pos]);

  const startResize = useCallback((e) => {
    e.stopPropagation(); e.preventDefault();
    isResizing.current  = true;
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, w: size.width, h: size.height };
  }, [size]);

  // Seed the allowance bar from the user's own ai_usage doc (rules allow
  // reading your own); after that each buzzChat reply carries the
  // authoritative post-request numbers. Caps mirror functions/src/buzzChat.ts.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'ai_usage', uid)).then(snap => {
      const d = snap.data();
      const today = d && d.day === new Date().toISOString().slice(0, 10) ? d : {};
      setAllowance({
        requestsUsed: today.requests ?? 0, requestsCap: 100,
        tokensUsed: today.tokens ?? 0, tokensCap: 250000,
      });
    }).catch(() => {}); // the bar is a nicety — never block chat on it
  }, []);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput('');

    const applyUpdate = (fn) => setSessions(ss => ss.map(s => s.id === currentId ? fn(s) : s));

    // Optimistically add user message
    let history = [];
    applyUpdate(s => {
      history = s.messages;
      return {
        ...s,
        title:    s.title === 'New chat' ? msg.slice(0, 48) : s.title,
        messages: [...s.messages, { role: 'user', text: msg }],
      };
    });

    setTyping(true);

    try {
      // Build API messages: include new user msg, drop leading assistant msgs (Anthropic requires user first)
      const fullHistory = [...history, { role: 'user', text: msg }];
      const firstUserIdx = fullHistory.findIndex(m => m.role === 'user');
      const apiMessages = fullHistory
        .slice(firstUserIdx >= 0 ? firstUserIdx : 0)
        .slice(-10)
        .map(m => ({ role: m.role, content: m.text }));

      // buzzChat: cheap Gemini tokens, server-side workspace-scoped context
      // (membership verified there), read-only, per-user metering + daily cap.
      const result = await callBuzz({
        workspaceId,
        messages: apiMessages,
      });

      const reply = result.data?.text ?? 'Sorry, I could not get a response.';
      if (result.data?.allowance) setAllowance(result.data.allowance);

      setTyping(false);
      applyUpdate(s => ({
        ...s,
        messages: [...s.messages, { role: 'assistant', text: reply }],
      }));
    } catch (err) {
      setTyping(false);
      const errText = err?.message?.includes('unauthenticated')
        ? 'Please sign in to use Buzz AI.'
        : err?.message?.includes('API key not configured')
        ? 'Buzz AI is not configured yet — ask your admin to set the API key.'
        : `Error: ${err?.message || 'Something went wrong.'}`;
      applyUpdate(s => ({
        ...s,
        messages: [...s.messages, { role: 'assistant', text: errText }],
      }));
    }
  }, [input, typing, currentId, workspaceId]);

  const newChat = useCallback(() => {
    const s = mkSession();
    setSessions(ss => [s, ...ss]);
    setCurrentId(s.id);
    setView('chat');
    setInput('');
  }, []);

  const openSession = useCallback((id) => {
    setCurrentId(id);
    setView('chat');
  }, []);

  const deleteSession = useCallback((id, e) => {
    e.stopPropagation();
    setSessions(ss => {
      const next = ss.filter(s => s.id !== id);
      if (next.length === 0) {
        const fresh = mkSession();
        setCurrentId(fresh.id);
        return [fresh];
      }
      if (id === currentId) setCurrentId(next[0].id);
      return next;
    });
  }, [currentId]);

  const showSuggestions = messages.length <= 1 && view === 'chat';

  return (
    <div className="buzzai-float" style={{ left: pos.x, top: pos.y, width: size.width, height: minimized ? undefined : size.height }}>
      {/* Draggable header */}
      <div className="buzzai-hdr" onMouseDown={startDrag}>
        <span className="buzzai-bee">🐝</span>
        <div className="buzzai-hdr-info">
          <span className="buzzai-name">Buzz AI</span>
          {!minimized && <span className="buzzai-subtitle">Pipeline assistant</span>}
        </div>
        <div className="buzzai-hdr-btns">
          <button
            className={`buzzai-hdr-btn${view === 'sessions' && !minimized ? ' active' : ''}`}
            title="Chat history"
            onClick={() => { if (minimized) { setMinimized(false); setView('sessions'); } else setView(v => v === 'sessions' ? 'chat' : 'sessions'); }}
          >🕐</button>
          <button className="buzzai-hdr-btn" title="New chat" onClick={newChat}>✚</button>
          <button className="buzzai-hdr-btn" title={minimized ? 'Expand' : 'Minimise'} onClick={() => setMinimized(m => !m)}>
            {minimized ? '□' : '−'}
          </button>
          <button className="buzzai-hdr-btn buzzai-close-btn" title="Close" onClick={onClose}>×</button>
        </div>
      </div>

      {!minimized && allowance && (
        <div className="buzz-allowance" data-testid="buzz-allowance">
          <div className="buzz-allowance-meta">
            <span>Daily allowance</span>
            <span>{allowance.requestsUsed}/{allowance.requestsCap} messages today</span>
          </div>
          <div className="buzz-allowance-track">
            <div
              className={`buzz-allowance-fill${Math.max(
                allowance.requestsUsed / allowance.requestsCap,
                allowance.tokensUsed / allowance.tokensCap) >= 0.9 ? ' hot' : ''}`}
              style={{ width: `${Math.min(100, Math.round(Math.max(
                allowance.requestsUsed / allowance.requestsCap,
                allowance.tokensUsed / allowance.tokensCap) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {!minimized && view === 'sessions' && (
        <div className="buzzai-sessions">
          <div className="buzzai-sessions-hd">
            <span className="buzzai-sessions-title">Chat history</span>
            <button className="buzzai-new-chat-btn" onClick={newChat}>+ New chat</button>
          </div>
          <div className="buzzai-sessions-list">
            {sessions.map(s => (
              <div
                key={s.id}
                className={`buzzai-sess-item${s.id === currentId ? ' active' : ''}`}
                onClick={() => openSession(s.id)}
              >
                <div className="buzzai-sess-main">
                  <div className="buzzai-sess-title">{s.title}</div>
                  <div className="buzzai-sess-meta">
                    {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' · '}{s.messages.length} msg{s.messages.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <button className="buzzai-sess-del" title="Delete chat" onClick={(e) => deleteSession(s.id, e)}>🗑</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!minimized && view === 'chat' && (
        <>
          <div className="buzzai-messages">
            {messages.map((m, i) => (
              <div key={i} className={`buzzai-msg ${m.role}`}>
                {m.role === 'assistant' && <span className="buzzai-avatar">🐝</span>}
                <div className="buzzai-bubble">{renderMarkdown(m.text)}</div>
              </div>
            ))}
            {typing && (
              <div className="buzzai-msg assistant">
                <span className="buzzai-avatar">🐝</span>
                <div className="buzzai-bubble buzzai-typing"><span /><span /><span /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {showSuggestions && (
            <div className="buzzai-suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} className="buzzai-suggestion" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          )}

          <div className="buzzai-input-row">
            <input
              className="buzzai-input"
              placeholder="Ask about your pipeline…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
            />
            <button className="buzzai-send" onClick={() => send()}>Send</button>
          </div>
        </>
      )}
      {!minimized && <div className="buzzai-resize-handle" onMouseDown={startResize} />}
    </div>
  );
}
